import { AnimatedTitle } from '@/components/animated-title'
import { ASCIIAnimation } from '@/components/ascii'
import { LandingAsciiVisual } from '@/components/landing/landing-ascii-visual'
import { cn } from '@/lib/utils'
import type { LandingHeroVisualConfig } from '@/types/client-landing'

const sharedLogoTitleClass =
  'inline-block h-8 aspect-[123/27] bg-foreground md:h-9'

type LandingHeroVisualProps = {
  visual?: LandingHeroVisualConfig
  logoPath?: string
  /** Fill parent box as 16:9 hero background (decorative; no separate aria on animation) */
  asBackground?: boolean
  className?: string
}

export function LandingHeroVisual({
  visual,
  logoPath,
  asBackground = false,
  className,
}: LandingHeroVisualProps) {
  const inner = renderInner(visual, logoPath, asBackground)
  if (!inner) return null
  if (asBackground) {
    return (
      <div className={cn('pointer-events-none absolute inset-0 h-full w-full', className)}>{inner}</div>
    )
  }
  return <div className={className}>{inner}</div>
}

function renderInner(
  visual: LandingHeroVisualConfig | undefined,
  logoPath?: string,
  fillParent = false
) {
  const type = visual?.type ?? (logoPath ? 'logo' : undefined)
  if (!type) return null

  switch (type) {
    case 'image':
      if (!visual || visual.type !== 'image' || !visual.src) return null
      return (
        <img
          src={visual.src}
          alt={visual.alt ?? ''}
          style={fillParent && visual.objectPosition ? { objectPosition: visual.objectPosition } : undefined}
          className={
            fillParent
              ? 'h-full w-full object-cover object-center'
              : 'max-h-64 w-full max-w-md rounded-lg object-contain'
          }
        />
      )
    case 'ascii':
      if (!visual || visual.type !== 'ascii' || !visual.artId) return null
      if (fillParent) {
        return (
          <div className="flex h-full w-full items-center justify-center opacity-35">
            <LandingAsciiVisual
              artId={visual.artId}
              decorative
              className="max-h-full max-w-full text-[0.45rem] sm:text-[0.55rem]"
            />
          </div>
        )
      }
      return (
        <LandingAsciiVisual
          artId={visual.artId}
          decorative={!visual.alt}
          ariaLabel={visual.alt}
        />
      )
    case 'asciiAnimation':
      if (!visual || visual.type !== 'asciiAnimation') return null
      if (fillParent) {
        return (
          <ASCIIAnimation
            frameFolder={visual.frameFolder}
            frameCount={visual.frameCount}
            fps={visual.fps}
            quality={visual.quality}
            lazy={visual.lazy}
            playOnHover={visual.playOnHover}
            fit="cover"
            className="h-full w-full"
          />
        )
      }
      return (
        <div className="relative h-56 w-full max-w-md sm:h-64 md:h-72">
          <ASCIIAnimation
            frameFolder={visual.frameFolder}
            frameCount={visual.frameCount}
            fps={visual.fps}
            quality={visual.quality}
            lazy={visual.lazy}
            playOnHover={visual.playOnHover}
            ariaLabel={visual.alt}
            className="h-full w-full"
          />
        </div>
      )
    case 'logo':
    default:
      if (logoPath) {
        if (fillParent) {
          return (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#e3f2ff] to-white opacity-100 dark:from-[var(--primary-dark)] dark:to-background">
              <img
                src={logoPath}
                alt={visual?.alt ?? ''}
                className="max-h-[min(70%,28rem)] w-auto max-w-[90%] object-contain opacity-25 dark:opacity-20 dark:brightness-0 dark:invert"
              />
            </div>
          )
        }
        return (
          <img
            src={logoPath}
            alt={visual?.alt ?? ''}
            className="max-h-28 w-auto object-contain dark:brightness-0 dark:invert md:max-h-32"
          />
        )
      }
      return (
        <div
          className={
            fillParent
              ? 'flex h-full w-full items-center justify-center opacity-30'
              : 'inline-flex scale-[1.05] md:scale-110'
          }
        >
          <AnimatedTitle logoOnLightBackground logoClassName={sharedLogoTitleClass} />
        </div>
      )
  }
}
